import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { Alert, App, Button, Flex, Skeleton, Text } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { useTranslation } from "@/hooks/useTranslation"
import { useAgentPlatform } from "@/pages/DeploymentsPage/application/useAgentPlatform"
import {
  DEFAULT_COPILOT_STUDIO_SETTINGS,
  isGuid
} from "@/pages/DeploymentsPage/application/useAgentPlatform.selector"
import {
  CloudConnectionDemoHint,
  getCloudConnectionStep
} from "@/pages/DeploymentsPage/components/molecules/CloudConnectionStatus"
import { DeployTab } from "@/pages/DeploymentsPage/components/molecules/DeployTab"
import {
  AdminConsent,
  ApplicationUser,
  FederatedCredential,
  ManagementApp,
  ThreatDetection,
  ThreatDetectionCredential
} from "./AppRegistrationSteps"
import { COPILOT_STUDIO_NAME } from "./CopilotStudioConnection.constants"
import { CopilotStudioSettingsFields } from "./CopilotStudioSettingsFields"
import { GuidInput } from "./GuidInput"

import type {
  CopilotStudioPreInstall,
  CopilotStudioSettings
} from "@/api/useDeployments.types"
import type { StepItem } from "@/atoms"

// Connecting a Microsoft tenant, the first or another one: the settings, the
// admin's steps, then the tenant and client ids
export const CopilotStudioSetup = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "deployments.copilotStudio"
  })
  const { t: tConnection } = useTranslation("translation", {
    keyPrefix: "deployments.cloudConnection"
  })
  const { t: tRoot } = useTranslation()
  const { message } = App.useApp()
  const navigate = useNavigate()
  const platform = useAgentPlatform("copilot-studio")
  const [settings, setSettings] = useState<CopilotStudioSettings>(
    DEFAULT_COPILOT_STUDIO_SETTINGS
  )
  const [preInstall, setPreInstall] = useState<CopilotStudioPreInstall>()
  const [azureTenantId, setAzureTenantId] = useState("")
  const [clientId, setClientId] = useState("")
  const { isPreInstallIdle } = platform

  const loadPreInstall = useCallback(
    async () => setPreInstall(await platform.preInstall(settings)),
    [platform, settings]
  )

  // The federated credential values show as soon as the step does
  useEffect(() => {
    if (!preInstall && isPreInstallIdle) loadPreInstall()
  }, [preInstall, isPreInstallIdle, loadPreInstall])

  const onConnect = async () => {
    const connected = await platform.connect({
      azureTenantId: azureTenantId.trim(),
      clientId: clientId.trim(),
      settings
    })

    if (!connected) return

    message.success(tConnection("connected", { name: COPILOT_STUDIO_NAME }))
    navigate(`/${NAMES_ROUTES.DEPLOYMENTS.ROOT}`)
  }

  const error = platform.error && (
    <Alert
      showIcon
      type="error"
      title={platform.error}
      closable={{ onClose: platform.clearError }}
    />
  )

  // The values come from the pre-install; without them the step offers a retry
  const withPreInstall = (
    render: (values: CopilotStudioPreInstall) => React.ReactNode
  ) => {
    if (preInstall) return render(preInstall)
    if (platform.isPreInstalling)
      return <Skeleton active paragraph={{ rows: 3 }} />

    return (
      <Flex vertical gap={12} align="flex-start">
        {error}
        <Button onClick={loadPreInstall}>{tRoot("general.refresh")}</Button>
      </Flex>
    )
  }

  // Runtime protection's steps: the credential and the consent first, then
  // the threat detection in the Power Platform admin center
  const threatDetectionSteps = [
    ...(preInstall?.threatDetectionAppId
      ? [
          getCloudConnectionStep(
            t("steps.adminConsentPrefix"),
            t("steps.adminConsent"),
            <AdminConsent
              appId={preInstall.threatDetectionAppId}
              azureTenantId={azureTenantId}
            />
          )
        ]
      : []),
    getCloudConnectionStep(
      t("steps.threatDetectionPrefix"),
      t("steps.threatDetection"),
      withPreInstall((values) => (
        <ThreatDetection preInstall={values} clientId={clientId} />
      ))
    )
  ]

  const threatDetectionCredential = (values: CopilotStudioPreInstall) => (
    <ThreatDetectionCredential
      preInstall={values}
      azureTenantId={azureTenantId}
    />
  )

  // The admin's steps in Entra ID and Power Platform
  const adminSteps = [
    getCloudConnectionStep(
      t("steps.appRegistrationPrefix"),
      t("steps.appRegistration"),
      // Its ids are known once it is created, and the steps after need them
      <Flex vertical gap={24}>
        {withPreInstall((values) => (
          <FederatedCredential preInstall={values} />
        ))}
        <Flex vertical gap={12}>
          <Text>{t("appRegistration.ids")}</Text>
          <GuidInput
            label={t("fields.tenantId")}
            value={azureTenantId}
            onChange={setAzureTenantId}
          />
          <GuidInput
            label={t("fields.clientId")}
            value={clientId}
            onChange={setClientId}
          />
        </Flex>
        {/* Its loading and failure show once, with the credential above */}
        {preInstall && threatDetectionCredential(preInstall)}
      </Flex>
    ),
    getCloudConnectionStep(
      t("steps.managementAppPrefix"),
      t("steps.managementApp"),
      <ManagementApp clientId={clientId} />
    ),
    getCloudConnectionStep(
      t("steps.applicationUserPrefix"),
      t("steps.applicationUser"),
      <ApplicationUser />
    ),
    ...threatDetectionSteps
  ]

  const connectStep = getCloudConnectionStep(
    t("steps.connectPrefix"),
    t("steps.connect"),
    <Flex vertical gap={12}>
      <Flex gap={8} align="center">
        <Button
          type="primary"
          loading={platform.isConnecting}
          disabled={
            platform.isDemo || !isGuid(azureTenantId) || !isGuid(clientId)
          }
          onClick={onConnect}
        >
          {t("connect")}
        </Button>
        {platform.isDemo && <CloudConnectionDemoHint />}
      </Flex>
      {preInstall && error}
    </Flex>
  )

  const steps: StepItem[] = [
    getCloudConnectionStep(
      t("steps.settingsPrefix"),
      t("steps.settings"),
      <CopilotStudioSettingsFields value={settings} onChange={setSettings} />
    ),
    ...adminSteps,
    connectStep
  ]

  return <DeployTab steps={steps} hideReadMoreLink />
}
